import { pageTitle } from '@palaccount/config';
import type { LeaveRequestResponse } from '@palaccount/contracts';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Ltr } from '@/components/amount';
import { Pager } from '@/components/pager';
import { Gated } from '@/components/permission-gate';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Typeahead } from '@/components/ui/typeahead';
import { usePagedState, usePageRange } from '@/lib/paging';
import { useCan } from '@/lib/permissions';
import { useCompanyToday, useDocumentDate } from '@/lib/session';
import { cn } from '@/lib/utils';

import {
  useAllBalances,
  useCancelLeave,
  useDecideLeave,
  useLeaveCalendar,
  useLeaveRequests,
  useLeaveTypes,
  useMyBalances,
  useSubmitLeave,
  useUpdateLeaveType,
} from './api';
import { employeeOptionIn, useEmployeePicker } from './employee-picker';
import { HrTabs } from './hr-tabs';
import { PersonSourceHint } from './person-source';

/** HR-04's two request lists — one block at a time (NFR-42). */
const LEAVE_PAGE_SIZE = 25;

function currentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * HR-04 — Leave Management: the permanent link banner, then Request /
 * Approvals / Balances / Team calendar / Policy. Balances are DERIVED —
 * the card prints the formula. Every ACTIVE type is offered to everybody
 * (BR-HR-04-19); ineligibility is shown WITH the type and its reason.
 */
export function LeavePage() {
  const { t } = useTranslation();
  const canView = useCan('hr.view');
  const canApprove = useCan('hr.approve');
  const [tab, setTab] = useState<'request' | 'approvals' | 'balances' | 'calendar' | 'policy'>('request');
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = pageTitle(t('hr.leaveTitle'));
  }, [t]);

  const tabs = [
    'request',
    ...(canApprove ? ['approvals'] : []),
    ...(canView ? ['balances', 'calendar'] : []),
    'policy',
  ] as const;

  return (
    <div className="grid gap-4">
      <HrTabs />
      {/* The permanent banner (HR-04 §4). */}
      <p className="rounded-md bg-primary/10 p-3 text-sm">{t('hr.leaveBanner')}</p>

      <div className="flex flex-wrap gap-2">
        {tabs.map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key as typeof tab)}
            className={cn(
              'rounded-md px-3 py-1.5 text-sm',
              tab === key ? 'bg-accent font-medium' : 'text-muted-foreground hover:bg-accent/50',
            )}
          >
            {t(`hr.leaveTab.${key}`)}
          </button>
        ))}
      </div>

      {notice ? <p className="rounded-md bg-primary/10 p-3 text-sm">{notice}</p> : null}
      {error ? (
        <p role="alert" className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </p>
      ) : null}

      {tab === 'request' ? (
        <RequestTab onNotice={setNotice} onError={setError} />
      ) : null}

      {tab === 'approvals' ? (
        <ApprovalsTab onNotice={setNotice} onError={setError} />
      ) : null}

      {tab === 'balances' ? <BalancesTab /> : null}
      {tab === 'calendar' ? <CalendarTab /> : null}
      {tab === 'policy' ? <PolicyTab onError={setError} /> : null}
    </div>
  );
}

function RequestTab({
  onNotice,
  onError,
}: {
  onNotice: (message: string) => void;
  onError: (message: string | null) => void;
}) {
  const { t, i18n } = useTranslation();
  const ar = i18n.language === 'ar';
  const { data: types } = useLeaveTypes();
  const balances = useMyBalances();
  /** NFR-42 — my own requests, one block at a time; the page is in the URL. */
  const minePaging = usePagedState(LEAVE_PAGE_SIZE, 'mine');
  const requests = useLeaveRequests('mine', minePaging.page, LEAVE_PAGE_SIZE);
  const mineRange = usePageRange(requests.data, LEAVE_PAGE_SIZE);
  const submit = useSubmitLeave();
  const cancel = useCancelLeave();

  const [employeeId, setEmployeeId] = useState('');
  // GEN-50 — HR-04 §5's "for whom" field, searched rather than holding the
  // payroll. `heldId` is what keeps a leaver's name on a form about them.
  const employeePicker = useEmployeePicker(
    employeeId === '' ? null : employeeId,
    {},
    employeeOptionIn(ar),
  );
  const [typeId, setTypeId] = useState('');
  // G-09: a new request's dates start as the company's today (a one-day leave).
  const today = useCompanyToday();
  const [startDate, setStartDate] = useDocumentDate();
  const [endDate, setEndDate] = useDocumentDate();
  const [reason, setReason] = useState('');
  const [documentName, setDocumentName] = useState('');

  const selectedType = (types ?? []).find((type) => type.id === (typeId || (types ?? [])[0]?.id));
  const effectiveTypeId = typeId || ((types ?? [])[0]?.id ?? '');
  const balance = (balances.data ?? []).find((b) => b.leaveTypeId === effectiveTypeId);
  const complete = effectiveTypeId !== '' && startDate !== '' && endDate !== '' && startDate <= endDate;

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <CardContent className="grid gap-3">
          <h3 className="text-sm font-semibold">{t('hr.newLeaveRequest')}</h3>
          <Gated permission="hr.create" reasonKey="perm.cannot.fileLeaveForOthers">
            <div className="grid gap-1">
              <Label htmlFor="leave-emp">{t('hr.employee')}</Label>
              {/* The empty entry is `clearLabel`: filing for MYSELF is the
                  default, and it is one of the rows the arrow keys walk. */}
              <Typeahead
                id="leave-emp"
                label={t('hr.employee')}
                value={employeeId === '' ? null : employeeId}
                onChange={(id) => setEmployeeId(id ?? '')}
                options={employeePicker.options}
                selected={employeePicker.optionFor(employeeId)}
                query={employeePicker.query}
                onQueryChange={employeePicker.setQuery}
                clearLabel={t('hr.myself')}
                placeholder={t('hr.myself')}
                emptyLabel={t('picker.noMatchingEmployee')}
                loading={employeePicker.loading}
              />
              {/* BR-HR-01-16: this control draws from EMPLOYEES — the leave
                  ledger is about employment, so a user account with no
                  employee record is rightly not offered here. */}
              <PersonSourceHint source="employees" />
            </div>
          </Gated>
          <div className="grid gap-1">
            <Label htmlFor="leave-type">{t('hr.leaveType')}</Label>
            <NativeSelect id="leave-type" value={effectiveTypeId} onChange={(e) => setTypeId(e.target.value)}>
              {(types ?? []).map((type) => (
                <option key={type.id} value={type.id}>
                  {ar ? type.nameAr : type.name}
                </option>
              ))}
            </NativeSelect>
          </div>
          {/* BR-HR-04-05/-19: an ineligible type is SHOWN, with the reason. */}
          {balance && !balance.eligible ? (
            <p className="text-xs text-amber-700 dark:text-amber-400">{balance.ineligibleReason}</p>
          ) : null}
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="grid gap-1">
              <Label htmlFor="leave-start">{t('hr.startDate')}</Label>
              <Input id="leave-start" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div className="grid gap-1">
              <Label htmlFor="leave-end">{t('hr.endDate')}</Label>
              <Input id="leave-end" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
          </div>
          <div className="grid gap-1">
            <Label htmlFor="leave-reason">{t('hr.leaveReason')}</Label>
            <Textarea id="leave-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} />
          </div>
          {selectedType?.requiresDocument ? (
            <div className="grid gap-1">
              <Label htmlFor="leave-doc" className="text-destructive">
                {t('hr.document')}
              </Label>
              <Input id="leave-doc" value={documentName} onChange={(e) => setDocumentName(e.target.value)} />
            </div>
          ) : null}
          <Button
            disabled={!complete}
            onClick={() => {
              onError(null);
              submit.mutate(
                {
                  ...(employeeId !== '' ? { employeeId } : {}),
                  leaveTypeId: effectiveTypeId,
                  startDate,
                  endDate,
                  ...(reason ? { reason } : {}),
                  ...(documentName ? { documentName } : {}),
                },
                {
                  onSuccess: (body) => {
                    onNotice(body.warnings[0] ?? t('hr.requestSubmitted'));
                    setStartDate(today);
                    setEndDate(today);
                    setReason('');
                    setDocumentName('');
                  },
                  onError: (cause) =>
                    onError(cause instanceof Error ? cause.message : t('common.saveFailed')),
                },
              );
            }}
          >
            {t('hr.submitRequest')}
          </Button>
        </CardContent>
      </Card>

      <div className="grid content-start gap-4">
        {balance ? (
          <Card>
            <CardContent className="grid gap-2">
              <h3 className="text-sm font-semibold">{t('hr.availableBalance')}</h3>
              <dl className="grid grid-cols-2 gap-2 text-sm">
                <BalanceCell label={t('hr.opening')} value={balance.opening} />
                <BalanceCell label={t('hr.accrued')} value={balance.accrued} />
                <BalanceCell label={t('hr.used')} value={balance.used} />
                <BalanceCell label={t('hr.pendingHold')} value={balance.pending} amber />
              </dl>
              <p className="rounded-md bg-primary/10 p-2 text-center text-sm font-bold">
                {t('hr.available')}: <Ltr value={balance.available} className="font-mono" />
              </p>
              {/* The formula, printed (HR-04 §5e). */}
              <p className="text-xs text-muted-foreground">{t('hr.balanceFormula')}</p>
            </CardContent>
          </Card>
        ) : null}

        <Card>
          <CardContent className="grid gap-2">
            <h3 className="text-sm font-semibold">{t('hr.myRequests')}</h3>
            {(requests.data?.requests ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('hr.noRequests')}</p>
            ) : (
              (requests.data?.requests ?? []).map((request) => (
                <div key={request.id} className="flex flex-wrap items-center gap-2 rounded-md border border-border p-2 text-sm">
                  <span className="font-medium">{request.leaveTypeName}</span>
                  <Ltr value={`${request.startDate} → ${request.endDate}`} className="font-mono text-xs" />
                  <span className="text-xs text-muted-foreground">
                    {t('hr.days', { count: Number(request.days) })}
                  </span>
                  <LeaveStatusPill status={request.status} />
                  {request.decisionComment ? (
                    <span className="w-full text-xs text-destructive">{request.decisionComment}</span>
                  ) : null}
                  {request.status === 'pending' ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="ms-auto"
                      onClick={() =>
                        cancel.mutate(request.id, {
                          onSuccess: () => onNotice(t('hr.cancelledReleased')),
                        })
                      }
                    >
                      {t('hr.cancelRequest')}
                    </Button>
                  ) : null}
                </div>
              ))
            )}
            {(requests.data?.total ?? 0) > 0 ? (
              <Pager
                page={minePaging.page}
                pages={mineRange.pages}
                first={mineRange.first}
                last={mineRange.last}
                total={mineRange.total}
                onPage={minePaging.setPage}
                label={t('hr.myRequests')}
              />
            ) : null}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function BalanceCell({ label, value, amber }: { label: string; value: string; amber?: boolean }) {
  return (
    <div className={cn('rounded-md p-2', amber ? 'bg-amber-500/10' : 'bg-muted/50')}>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="font-mono font-medium">{value}</div>
    </div>
  );
}

export function LeaveStatusPill({ status }: { status: LeaveRequestResponse['status'] }) {
  const { t } = useTranslation();
  const tone =
    status === 'pending'
      ? 'bg-amber-500/10 text-amber-700 dark:text-amber-400'
      : status === 'approved'
        ? 'bg-green-500/10 text-green-700 dark:text-green-400'
        : status === 'rejected'
          ? 'bg-destructive/10 text-destructive'
          : 'bg-muted text-muted-foreground';
  return (
    <span className={cn('rounded-full px-2 py-0.5 text-[11px] font-medium', tone)}>
      {t(`hr.leave.${status}`)}
    </span>
  );
}

function ApprovalsTab({
  onNotice,
  onError,
}: {
  onNotice: (message: string) => void;
  onError: (message: string | null) => void;
}) {
  const { t } = useTranslation();
  /** NFR-42 — the approvals queue, one block at a time. */
  const paging = usePagedState(LEAVE_PAGE_SIZE, 'approvals');
  const requests = useLeaveRequests('approvals', paging.page, LEAVE_PAGE_SIZE);
  const range = usePageRange(requests.data, LEAVE_PAGE_SIZE);
  const decide = useDecideLeave();
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [comment, setComment] = useState('');

  return (
    <Card>
      <CardContent className="grid gap-3">
        <h3 className="text-sm font-semibold">{t('hr.leaveTab.approvals')}</h3>
        {(requests.data?.requests ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('hr.noRequests')}</p>
        ) : (
          (requests.data?.requests ?? []).map((request) => (
            <div key={request.id} className="grid gap-2 rounded-md border border-border p-3">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="font-medium">{request.employeeName}</span>
                <span>{request.leaveTypeName}</span>
                <Ltr value={`${request.startDate} → ${request.endDate}`} className="font-mono text-xs" />
                <span className="text-xs text-muted-foreground">
                  {t('hr.days', { count: Number(request.days) })}
                </span>
              </div>
              {request.reason ? <p className="text-xs text-muted-foreground">{request.reason}</p> : null}
              {/* BR-HR-04-16: team-scoped overlap warning. */}
              {request.overlaps.length > 0 ? (
                <p className="rounded-md bg-amber-500/10 p-2 text-xs text-amber-700 dark:text-amber-400">
                  {t('hr.overlapWarning')}: {request.overlaps.join(', ')}
                </p>
              ) : null}
              {rejectingId === request.id ? (
                <div className="grid gap-2">
                  <p className="text-xs">{t('hr.rejectCommentNote')}</p>
                  <Textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2} />
                  <div className="flex gap-2">
                    <Button
                      variant="destructive"
                      size="sm"
                      disabled={comment.trim() === ''}
                      onClick={() => {
                        onError(null);
                        decide.mutate(
                          { requestId: request.id, approve: false, comment },
                          {
                            onSuccess: () => {
                              setRejectingId(null);
                              setComment('');
                              onNotice(t('hr.rejectedReleased'));
                            },
                            onError: (cause) =>
                              onError(cause instanceof Error ? cause.message : t('common.saveFailed')),
                          },
                        );
                      }}
                    >
                      {t('hr.rejectRequest')}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setRejectingId(null)}>
                      {t('common.cancel')}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => {
                      onError(null);
                      decide.mutate(
                        { requestId: request.id, approve: true },
                        {
                          onSuccess: (body) =>
                            onNotice((body as { message?: string }).message ?? t('hr.approvedFlows')),
                          onError: (cause) =>
                            onError(cause instanceof Error ? cause.message : t('common.saveFailed')),
                        },
                      );
                    }}
                  >
                    {t('hr.approve')}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setRejectingId(request.id)}>
                    {t('hr.reject')}
                  </Button>
                </div>
              )}
            </div>
          ))
        )}
        {(requests.data?.total ?? 0) > 0 ? (
          <Pager
            page={paging.page}
            pages={range.pages}
            first={range.first}
            last={range.last}
            total={range.total}
            onPage={paging.setPage}
            label={t('hr.leaveTab.approvals')}
          />
        ) : null}
      </CardContent>
    </Card>
  );
}

/** A page of people. 50 is the product's settled block (DEFAULT_PAGE_SIZE). */
const BALANCE_PAGE_SIZE = 50;

function BalancesTab() {
  const { t, i18n } = useTranslation();
  const ar = i18n.language === 'ar';
  // The page in the URL, so a link to page 3 of the balance table is a link
  // (paging.ts property 1), and `paging.change` around the search box so a new
  // search starts at page 1 (BR-DSH-09-19) — through the setter, never an
  // effect.
  const paging = usePagedState(BALANCE_PAGE_SIZE, 'balances');
  const [q, setQ] = useState('');
  const { data } = useAllBalances(true, paging.page, paging.pageSize, q);
  const range = usePageRange(data, paging.pageSize);
  if (!data) return <div className="h-40 animate-pulse rounded-md bg-muted" aria-busy />;
  return (
    <Card>
      <CardContent className="grid gap-3 overflow-x-auto">
        <Input
          value={q}
          onChange={(e) => paging.change(setQ)(e.target.value)}
          placeholder={t('hr.searchEmployees')}
          aria-label={t('hr.searchEmployees')}
          className="h-9 max-w-xs"
        />
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('hr.employee')}</TableHead>
              {/* A column for EVERY active type (BR-HR-04-19). */}
              {data.types.map((type) => (
                <TableHead key={type.id} className="text-end">
                  {ar ? type.nameAr : type.name}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.rows.map((row) => (
              <TableRow key={row.employeeId}>
                <TableCell>{ar ? row.fullNameAr : row.fullName}</TableCell>
                {data.types.map((type) => {
                  const balance = row.balances.find((b) => b.leaveTypeId === type.id);
                  return (
                    <TableCell key={type.id} className="text-end font-mono" title={balance?.ineligibleReason ?? undefined}>
                      {balance?.available ?? '—'}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Pager
          page={data.page}
          pages={range.pages}
          first={range.first}
          last={range.last}
          total={range.total}
          onPage={paging.setPage}
          label={t('hr.balancePages')}
        />
      </CardContent>
    </Card>
  );
}

function CalendarTab() {
  const { t, i18n } = useTranslation();
  const ar = i18n.language === 'ar';
  const [month, setMonth] = useState(currentMonth());
  const { data } = useLeaveCalendar(month, true);

  return (
    <Card>
      <CardContent className="grid gap-3 overflow-x-auto">
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">{t('hr.calendarHint')}</p>
          <Input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="h-9 w-44"
            aria-label={t('hr.month')}
          />
        </div>
        {data ? (
          <table className="text-xs">
            <tbody>
              {data.rows.map((row) => (
                <tr key={row.employeeId}>
                  <td className="whitespace-nowrap p-1 pe-3 text-sm">{ar ? row.fullNameAr : row.fullName}</td>
                  {row.days.map((state, i) => (
                    <td key={i} className="p-0.5">
                      <span
                        title={`${month}-${String(i + 1).padStart(2, '0')}`}
                        className={cn(
                          'block size-5 rounded',
                          state === 'approved'
                            ? 'bg-blue-500/60'
                            : state === 'pending'
                              ? 'bg-amber-400/50 [background-image:repeating-linear-gradient(45deg,transparent,transparent_2px,rgba(255,255,255,.5)_2px,rgba(255,255,255,.5)_4px)]'
                              : state === 'off'
                                ? 'bg-muted'
                                : 'bg-muted/30',
                        )}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="h-32 animate-pulse rounded-md bg-muted" aria-busy />
        )}
      </CardContent>
    </Card>
  );
}

function PolicyTab({
  onError,
}: {
  onError: (message: string | null) => void;
}) {
  const { t, i18n } = useTranslation();
  const ar = i18n.language === 'ar';
  const { data: types } = useLeaveTypes();
  const update = useUpdateLeaveType();

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <p className="col-span-full text-xs text-muted-foreground">{t('hr.policyHint')}</p>
      {(types ?? []).map((type) => (
        <Card key={type.id}>
          <CardContent className="grid gap-2 text-sm">
            <h3 className="font-semibold">{ar ? type.nameAr : type.name}</h3>
            <dl className="grid gap-1">
              <div className="flex justify-between">
                <dt className="text-muted-foreground">{t('hr.accrual')}</dt>
                <dd className="font-mono">
                  {type.accrualRate} / {t(`hr.period.${type.accrualPeriod}`)}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">{t('hr.opening')}</dt>
                <dd className="font-mono">{type.openingBalance}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">{t('hr.minTenure')}</dt>
                <dd className="font-mono">{type.minTenureMonths}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">{t('hr.documentRule')}</dt>
                <dd>
                  {type.requiresDocument
                    ? type.documentThresholdDays !== null
                      ? t('hr.docBeyondDays', { count: type.documentThresholdDays })
                      : t('hr.docAlways')
                    : t('hr.docNever')}
                </dd>
              </div>
            </dl>
            <Gated permission="admin.edit" reasonKey="perm.cannot.configureLeave">
              <MinTenureEditor
                value={type.minTenureMonths}
                onSave={(minTenureMonths) => {
                  onError(null);
                  update.mutate(
                    { leaveTypeId: type.id, body: { minTenureMonths } },
                    {
                      onError: (cause) =>
                        onError(cause instanceof Error ? cause.message : t('common.saveFailed')),
                    },
                  );
                }}
              />
            </Gated>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function MinTenureEditor({ value, onSave }: { value: number; onSave: (months: number) => void }) {
  const { t } = useTranslation();
  const [months, setMonths] = useState(String(value));
  return (
    <div className="flex items-center gap-2">
      <Input
        type="number"
        min={0}
        value={months}
        onChange={(e) => setMonths(e.target.value)}
        className="h-9 w-24"
        aria-label={t('hr.minTenure')}
      />
      <Button variant="outline" size="sm" disabled={months === String(value)} onClick={() => onSave(Number(months))}>
        {t('common.save')}
      </Button>
    </div>
  );
}
